import React, { createContext, useContext, ReactNode } from 'react';
import { useAuthenticator } from '@aws-amplify/ui-react';
import { useQuery } from '@apollo/client';
import { GET_USER_ID_BY_AUTH_ID, GetUserIdByAuthIdResult } from '../graphql/queries/user';

interface UserContextValue {
  userId: number | null;
  userEmail: string | null;
  isLoading: boolean;
  error: Error | null;
}

const UserContext = createContext<UserContextValue | undefined>(undefined);

export function UserProvider({ children }: { children: ReactNode }) {
  const { user, authStatus } = useAuthenticator();

  // Strip GoogleWorkspace_ prefix from username to get the email
  const authId = user?.username?.replace('GoogleWorkspace_', '');

  const { data, loading, error } = useQuery<GetUserIdByAuthIdResult>(GET_USER_ID_BY_AUTH_ID, {
    variables: { authId },
    skip: !authId || authStatus !== 'authenticated',
    fetchPolicy: 'cache-first',
  });

  // Show loading while:
  // 1. Authentication is still configuring
  // 2. User is authenticated but we're still loading their user ID
  // 3. User is authenticated but query hasn't been attempted yet
  const isLoading = authStatus === 'configuring' || (authStatus === 'authenticated' && !data && !error);

  // Debug logging
  console.log('[UserContext] State:', {
    authStatus,
    username: user?.username,
    authId,
    queryData: data,
    queryLoading: loading,
    queryError: error,
    isLoading,
    willSkipQuery: !authId || authStatus !== 'authenticated',
  });

  return (
    <UserContext.Provider
      value={{
        userId: data?.userIdByAuthId ?? null,
        userEmail: authId ?? null,
        isLoading,
        error: error || null,
      }}
    >
      {children}
    </UserContext.Provider>
  );
}

export function useUser() {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
}
